import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { CheckCircle2, XCircle, Info } from 'lucide-react';

type Kind = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  text: string;
  kind: Kind;
}

const Ctx = createContext<{ show: (text: string, kind?: Kind) => void }>({ show: () => undefined });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const show = useCallback((text: string, kind: Kind = 'info') => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, text, kind }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), kind === 'error' ? 6000 : 3000);
  }, []);
  return (
    <Ctx.Provider value={{ show }}>
      {children}
      <div className="no-print pointer-events-none fixed inset-x-0 bottom-24 z-[100] flex flex-col items-center gap-2 px-4 md:bottom-6" aria-live="polite">
        {items.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto flex max-w-md items-center gap-3 rounded-2xl px-5 py-3.5 text-lg font-medium text-white shadow-xl ${
              t.kind === 'success' ? 'bg-st-green' : t.kind === 'error' ? 'bg-st-rose' : 'bg-ink'
            }`}
          >
            {t.kind === 'success' ? <CheckCircle2 className="size-6 shrink-0" /> : t.kind === 'error' ? <XCircle className="size-6 shrink-0" /> : <Info className="size-6 shrink-0" />}
            <span>{t.text}</span>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);
